import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) { setState('error'); return; }

    let timer;
    const poll = async () => {
      if (attempts.current >= MAX_ATTEMPTS) { setState('pending'); return; }
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(res.data.payment_status)) { setState('error'); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {state === 'checking' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-600" />
            <h1 className="mt-5 text-xl font-bold text-slate-900">Confirming your payment...</h1>
            <p className="mt-1 text-sm text-slate-500">This will only take a moment.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
            <h1 className="mt-5 text-xl font-bold text-slate-900">Payment successful!</h1>
            <p className="mt-1 text-sm text-slate-500">Your plan is now active. Welcome aboard.</p>
            <Button onClick={() => navigate('/')} className="mt-6 w-full bg-blue-600 hover:bg-blue-700">Go to Dashboard <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-5 text-xl font-bold text-slate-900">Payment is processing</h1>
            <p className="mt-1 text-sm text-slate-500">It's taking a little longer than usual. Your plan will activate shortly.</p>
            <Button onClick={() => navigate('/')} variant="outline" className="mt-6 w-full">Back to Dashboard</Button>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100"><XCircle className="h-8 w-8 text-red-600" /></div>
            <h1 className="mt-5 text-xl font-bold text-slate-900">Something went wrong</h1>
            <p className="mt-1 text-sm text-slate-500">We couldn't confirm your payment. Please try again.</p>
            <Button onClick={() => navigate('/billing')} className="mt-6 w-full bg-blue-600 hover:bg-blue-700">Back to Plans</Button>
          </>
        )}
      </div>
    </div>
  );
}
